'use client'

import { useState } from "react"
import { Check, Copy, Gift } from "lucide-react"
import QRCode from "react-qr-code"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface InviteLink {
  id: number
  code: string
  benefit: {
    description: string
    free_period_for_referral: number
    free_period_for_user: number
  }
  is_active: boolean
  expires_at: string | null
}

interface ReferralLinkCardProps {
  link: InviteLink
  domain: string
  index?: number
  featured?: boolean
  showQr?: boolean
  /** Есть место поставить QR рядом со ссылкой, а не под ней — только когда карточка одна на всю ширину. */
  wide?: boolean
}

/** Одна реферальная ссылка целиком: свои преимущества + свой код + свой статус — не invite_links[0] для всех. */
export function ReferralLinkCard({ link, domain, index = 0, featured = false, showQr = false, wide = false }: ReferralLinkCardProps) {
  const [copied, setCopied] = useState(false)
  const url = `${domain}/?referral_code=${link.code}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Скопировано в буфер обмена')
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-xl border bg-gray-800/50 backdrop-blur-sm p-5 lg:p-6 transition-all duration-300 hover:bg-gray-800/70",
        featured
          ? "border-purple-500/40 shadow-[0_0_30px_-10px_rgba(168,85,247,0.35)]"
          : "border-gray-700"
      )}
    >
      {featured && (
        <>
          <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-purple-400/40 animate-pulse" />
          <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-gradient-to-br from-purple-500/25 via-purple-400/10 to-transparent blur-3xl animate-pulse" />
        </>
      )}

      <div className="relative flex items-center gap-2 text-gray-200 font-medium mb-4">
        <Gift className={cn("w-5 h-5", featured ? "text-purple-400" : "text-gray-500")} />
        {featured ? "Ваша реферальная ссылка" : `Ссылка #${index + 1}`}
      </div>

      <div className={cn("relative flex flex-col gap-4", showQr && wide && "sm:flex-row sm:items-start")}>
        <div className="flex-1 min-w-0">
          {link.benefit?.description && (
            <p className="text-white leading-relaxed mb-4">{link.benefit.description}</p>
          )}

          <div className="flex items-center gap-2 min-w-0">
            <code className="flex-1 min-w-0 p-2.5 rounded-lg bg-black/30 text-sm text-white truncate">
              {url}
            </code>
            <Button variant="outline" size="icon" onClick={copy} className="shrink-0 border-gray-600">
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>

          {link.expires_at && (
            <p className="text-gray-500 text-xs mt-3">
              Действует до {new Date(link.expires_at).toLocaleDateString('ru-RU')}
            </p>
          )}
        </div>

        {showQr && (
          <div className={cn("shrink-0 bg-white p-2.5 rounded-lg self-center", wide && "sm:self-start")}>
            <QRCode value={url} size={112} className="h-auto w-[112px]" />
          </div>
        )}
      </div>
    </div>
  )
}
